import React, { useState } from 'react';
import SiteReadinessCard from '../../../components/admin/SiteReadinessCard';
import InlineEditModeCard from '../../../components/admin/InlineEditModeCard';
import { Trash2, ExternalLink, UserPlus, RefreshCcw } from 'lucide-react';
import { api } from '../../../lib/api';
import StageChanger from '../../../components/crm/StageChanger';
import ContactForm from '../../../components/crm/ContactForm';
import ConfirmDialog from '../../../components/admin/ConfirmDialog';
import ReconcileProspectModal from './ReconcileProspectModal';

export default function OverviewTab({ account, role, onChange, onDeleted }) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(account.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [promoteContact, setPromoteContact] = useState(null); // preset for ContactForm
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const isAdmin = role === 'nexsav_admin';
  const showReconcile = isAdmin && account.type === 'school' && !account.typeSchoolDbn;

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await api.put(`/api/admin/accounts/${account.id}`, { notes: notesDraft });
      onChange?.(res.account);
      setEditingNotes(false);
    } catch (err) {
      alert(err.message || 'Failed');
    } finally {
      setSavingNotes(false);
    }
  };

  const doDelete = async () => {
    await api.delete(`/api/admin/accounts/${account.id}`);
    onDeleted?.();
  };

  const promotePrincipal = () => {
    setPromoteContact({
      name: account.typeSchoolPrincipalName || '',
      email: account.typeSchoolPrincipalEmail || '',
      role: 'principal',
      isDecisionMaker: true,
      preferredChannel: 'email',
    });
  };
  const promoteSuper = () => {
    setPromoteContact({
      name: account.typeSchoolSuperintendentName || '',
      email: account.typeSchoolSuperintendentEmail || '',
      role: 'superintendent',
      isDecisionMaker: false,
      preferredChannel: 'email',
    });
  };

  return (
    <div className="space-y-6">
      <Card title="Identity">
        <KV label="Stage">
          <StageChanger account={account} role={role} onChanged={onChange} />
        </KV>
        <KV label="Type"><span className="text-gray-300">{account.type || '—'}</span></KV>
        {account.siteUrl && (
          <KV label="Site">
            <a href={account.siteUrl.startsWith('http') ? account.siteUrl : `https://${account.siteUrl}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] text-gray-200 hover:text-white">
              {account.siteUrl} <ExternalLink className="w-3 h-3" />
            </a>
          </KV>
        )}
        {account.cfZoneId && <KV label="CF Zone"><span className="text-[12px] font-mono text-gray-500">{account.cfZoneId}</span></KV>}
        {account.lastContactedAt && <KV label="Last contacted"><span className="text-gray-400">{String(account.lastContactedAt).slice(0, 16)}</span></KV>}
        {isAdmin && 'contractValue' in account && (
          <KV label="Contract value">
            <span className="text-gray-300">{account.contractValue != null ? `$${(account.contractValue / 100).toFixed(2)}` : '—'}</span>
          </KV>
        )}
      </Card>

      <SiteReadinessCard account={account} />

      <InlineEditModeCard account={account} onChange={onChange} />

      {account.type === 'school' && (
        <Card
          title="DOE metadata"
          action={showReconcile && (
            <button
              onClick={() => setReconcileOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20"
            >
              <RefreshCcw className="w-3 h-3" /> Reconcile prospect
            </button>
          )}
        >
          <KV label="DBN"><span className="font-mono text-gray-300">{account.typeSchoolDbn || <span className="text-gray-600">not set</span>}</span></KV>
          <KV label="District"><span className="text-gray-300">{account.typeSchoolDistrict || '—'}</span></KV>
          <KV label="Borough"><span className="text-gray-300">{account.typeSchoolBorough || '—'}</span></KV>
          {account.typeSchoolAddress && <KV label="Address"><span className="text-gray-300">{account.typeSchoolAddress}, {account.typeSchoolCity} {account.typeSchoolZip}</span></KV>}
          {account.typeSchoolPhone && <KV label="Phone"><span className="text-gray-300">{account.typeSchoolPhone}</span></KV>}
          <div className="border-t border-white/[0.05] my-3" />
          <KV label="Principal">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[13px] text-gray-300">{account.typeSchoolPrincipalName || <span className="text-gray-600">—</span>}</div>
                {account.typeSchoolPrincipalEmail && <div className="text-[11px] text-gray-500">{account.typeSchoolPrincipalEmail}</div>}
              </div>
              {account.typeSchoolPrincipalName && (
                <button onClick={promotePrincipal} className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] bg-[#B0FC38]/10 border border-[#B0FC38]/20 text-[#B0FC38] hover:bg-[#B0FC38]/20">
                  <UserPlus className="w-3 h-3" /> Promote
                </button>
              )}
            </div>
          </KV>
          <KV label="Superintendent">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[13px] text-gray-300">{account.typeSchoolSuperintendentName || <span className="text-gray-600">—</span>}</div>
                {account.typeSchoolSuperintendentEmail && <div className="text-[11px] text-gray-500">{account.typeSchoolSuperintendentEmail}</div>}
              </div>
              {account.typeSchoolSuperintendentName && (
                <button onClick={promoteSuper} className="ml-auto inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] bg-[#B0FC38]/10 border border-[#B0FC38]/20 text-[#B0FC38] hover:bg-[#B0FC38]/20">
                  <UserPlus className="w-3 h-3" /> Promote
                </button>
              )}
            </div>
          </KV>
        </Card>
      )}

      <Card title="Notes" action={
        !editingNotes && (
          <button onClick={() => { setNotesDraft(account.notes || ''); setEditingNotes(true); }} className="text-[11px] text-gray-400 hover:text-white">Edit</button>
        )
      }>
        {editingNotes ? (
          <div className="space-y-2">
            <textarea rows={5} value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} className="w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#B0FC38]/40" />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setEditingNotes(false)} className="text-[12px] text-gray-500 hover:text-white">Cancel</button>
              <button onClick={saveNotes} disabled={savingNotes} className="px-3 py-1 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">{savingNotes ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-gray-300 whitespace-pre-wrap leading-relaxed">
            {account.notes || <span className="text-gray-600">No notes yet.</span>}
          </div>
        )}
      </Card>

      {isAdmin && (
        <Card title="Danger zone">
          <button
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20"
          >
            <Trash2 className="w-3 h-3" /> Delete account
          </button>
          <div className="text-[11px] text-gray-500 mt-2">Cascades to contacts, interactions, tasks, tickets, edit requests, and pages. Accounts with issued invoices, payments or service terms can't be deleted — mark them churned instead.</div>
        </Card>
      )}

      {promoteContact && (
        <ContactForm
          open
          accountId={account.id}
          accountType={account.type}
          initial={promoteContact}
          onClose={() => setPromoteContact(null)}
          onSaved={() => { setPromoteContact(null); }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title="Delete account?"
        description={`This permanently removes ${account.name} and all linked CRM data.`}
        confirmLabel="Delete"
        destructive
      />

      {reconcileOpen && (
        <ReconcileProspectModal
          open
          account={account}
          onClose={() => setReconcileOpen(false)}
          onReconciled={(updated) => { setReconcileOpen(false); onChange?.(updated); }}
        />
      )}
    </div>
  );
}

function Card({ title, action, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-white">{title}</h3>
        {action}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function KV({ label, children }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 gap-3 py-1.5 border-b border-white/[0.03] last:border-b-0">
      <div className="text-[11px] text-gray-500 uppercase tracking-wider pt-1">{label}</div>
      <div className="col-span-2 md:col-span-3 text-[13px]">{children}</div>
    </div>
  );
}
